import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { GridApiPro, GridCallbackDetails, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useRef, useState } from 'react';

export const useBulkSelection = <SelectedRowType, ResponseType extends Record<string, SelectedRowType[]>>({
  fetchContents,
  dataKey,
  pageSize = DATA_GRID_LAZY_PAGE_SIZE,
  gridApi,
  selectAllAcrossPages = false,
}: {
  fetchContents?: (parameters: {
    page: number;
    perPage: number;
  }) => Promise<ResponseType & { pageData: { total: number } }>;
  dataKey?: keyof ResponseType;
  pageSize?: number;
  gridApi?: React.RefObject<GridApiPro | null | undefined>;
  selectAllAcrossPages?: boolean;
} = {}) => {
  const [selectedRows, setSelectedRows] = useState<SelectedRowType[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel | undefined>();
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const latestRequest = useRef(0);

  const gridApiInstance = gridApi?.current;
  useEffect(() => {
    if (!selectAllAcrossPages || !gridApiInstance?.subscribeEvent) return;
    return gridApiInstance.subscribeEvent('headerSelectionCheckboxChange', ({ value }) => {
      const current = gridApiInstance.state.rowSelection;
      const isAlreadyAll = current.type === 'exclude' && current.ids.size === 0;
      if (value && isAlreadyAll) return;
      gridApiInstance.setRowSelectionModel({ type: value ? 'exclude' : 'include', ids: new Set() });
    });
  }, [gridApiInstance, selectAllAcrossPages]);

  const handleSelectionChange = async (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => {
    latestRequest.current += 1;
    const request = latestRequest.current;
    const isStale = () => request !== latestRequest.current;
    setSelectionModel(rowSelectionModel);
    const isSelectAll = rowSelectionModel.type === 'exclude' && rowSelectionModel.ids.size === 0;

    if (isSelectAll && fetchContents && dataKey) {
      setIsLoadingAll(true);
      try {
        const allItems: SelectedRowType[] = [];
        const firstPageResponse = await fetchContents({ page: 1, perPage: pageSize });
        const totalCount = firstPageResponse.pageData.total;

        const firstPageItems = firstPageResponse[dataKey];
        allItems.push(...firstPageItems);

        if (totalCount > 0) {
          const totalPages = Math.ceil(totalCount / pageSize);

          for (let page = 2; page <= totalPages && !isStale(); page += 1) {
            const response = await fetchContents({ page, perPage: pageSize });
            const items = response[dataKey];
            allItems.push(...items);
          }
        }

        if (!isStale()) setSelectedRows(allItems);
      } finally {
        if (!isStale()) setIsLoadingAll(false);
      }
    } else if (rowSelectionModel.type === 'exclude') {
      const allRowIds = details.api.getAllRowIds();
      const excludedIds = new Set(rowSelectionModel.ids);

      const selectedItems = allRowIds
        .filter(id => !excludedIds.has(id))
        .map(id => details.api.getRow(id))
        .filter(Boolean);

      setSelectedRows(selectedItems);
      setIsLoadingAll(false);
    } else {
      const selectedItems = [...rowSelectionModel.ids].map(id => details.api.getRow(id)).filter(Boolean);
      setSelectedRows(selectedItems);
      setIsLoadingAll(false);
    }
  };

  const clearSelection = () => {
    latestRequest.current += 1;
    setSelectedRows([]);
    setSelectionModel(undefined);
    setIsLoadingAll(false);
    gridApi?.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
  };

  return {
    selectedRows,
    selectionModel,
    isLoadingAll,
    handleSelectionChange: (...arguments_: Parameters<typeof handleSelectionChange>) =>
      void handleSelectionChange(...arguments_),
    setSelectedRows,
    clearSelection,
  };
};
